import React, { useEffect, useState } from 'react';
import { db, functions } from '../firebase';
import { collection, query, where, getDocs, updateDoc, doc, orderBy, limit } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import toast from 'react-hot-toast';

export const FraudPage = () => {
  const [flaggedRides, setFlaggedRides] = useState([]);
  const [suspiciousDrivers, setSuspiciousDrivers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch flagged rides
  useEffect(() => {
    const fetchFlaggedRides = async () => {
      try {
        const q = query(
          collection(db, 'rides'),
          where('flaggedForFraud', '==', true),
          orderBy('flaggedAt', 'desc'),
          limit(50)
        );
        const snapshot = await getDocs(q);
        const rides = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setFlaggedRides(rides);
      } catch (err) {
        console.error('Error fetching flagged rides:', err);
        toast.error('Failed to load flagged rides');
      }
    };

    const fetchSuspiciousDrivers = async () => {
      try {
        // Example: drivers with high cancellation rate or fraud score > 0.5
        // This is a placeholder – adjust based on your actual data structure
        const driversSnap = await getDocs(collection(db, 'drivers'));
        const drivers = [];
        for (const driverDoc of driversSnap.docs) {
          const driver = { id: driverDoc.id, ...driverDoc.data() };
          // For demo, treat drivers with `suspicious: true` or `fraudScore > 0.7`
          if (driver.suspicious === true || (driver.fraudScore && driver.fraudScore > 0.7)) {
            drivers.push(driver);
          }
        }
        setSuspiciousDrivers(drivers);
      } catch (err) {
        console.error(err);
      }
    };

    Promise.all([fetchFlaggedRides(), fetchSuspiciousDrivers()]).finally(() => setLoading(false));
  }, []);

  const markRideInvestigated = async (rideId) => {
    try {
      await updateDoc(doc(db, 'rides', rideId), {
        flaggedForFraud: false,
        fraudInvestigatedAt: new Date(),
        fraudInvestigatedBy: 'admin'
      });
      toast.success('Ride marked as investigated');
      setFlaggedRides(prev => prev.filter(ride => ride.id !== rideId));
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const suspendDriver = async (driverId) => {
    if (!window.confirm('Suspend this driver? They will be unable to go online.')) return;
    const reason = prompt('Reason for suspension:');
    if (!reason) return;
    const suspendDriverFn = httpsCallable(functions, 'suspendDriver');
    try {
      await suspendDriverFn({ driverId, reason });
      toast.success('Driver suspended');
      // Refresh suspicious drivers list
      setSuspiciousDrivers(prev => prev.filter(d => d.id !== driverId));
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (loading) return <div className="flex justify-center p-8"><div className="text-gray-400">Loading fraud data...</div></div>;

  return (
    <div className="space-y-6">
      {/* Flagged Rides Section */}
      <div className="bg-gray-800 rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4">Flagged Rides</h2>
        {flaggedRides.length === 0 ? (
          <p className="text-gray-400">No flagged rides.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-4 py-2 text-left">Ride ID</th>
                  <th className="px-4 py-2 text-left">User</th>
                  <th className="px-4 py-2 text-left">Driver</th>
                  <th className="px-4 py-2 text-left">Reason</th>
                  <th className="px-4 py-2 text-left">Flagged At</th>
                  <th className="px-4 py-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {flaggedRides.map(ride => (
                  <tr key={ride.id} className="border-t border-gray-700">
                    <td className="px-4 py-2 font-mono text-xs">{ride.id}</td>
                    <td className="px-4 py-2">{ride.userId?.slice(-8) || '-'}</td>
                    <td className="px-4 py-2">{ride.driverId?.slice(-8) || '-'}</td>
                    <td className="px-4 py-2">{ride.fraudReason || 'Manual flag'}</td>
                    <td className="px-4 py-2">{ride.flaggedAt?.toDate().toLocaleString() || '-'}</td>
                    <td className="px-4 py-2">
                      <button
                        onClick={() => markRideInvestigated(ride.id)}
                        className="bg-emerald-600 hover:bg-emerald-500 px-2 py-1 rounded text-xs"
                      >
                        Mark Investigated
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Suspicious Drivers Section */}
      <div className="bg-gray-800 rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4">Suspicious Drivers</h2>
        {suspiciousDrivers.length === 0 ? (
          <p className="text-gray-400">No suspicious drivers.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-4 py-2 text-left">Driver ID</th>
                  <th className="px-4 py-2 text-left">Name</th>
                  <th className="px-4 py-2 text-left">Fraud Score</th>
                  <th className="px-4 py-2 text-left">Reason</th>
                  <th className="px-4 py-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {suspiciousDrivers.map(driver => (
                  <tr key={driver.id} className="border-t border-gray-700">
                    <td className="px-4 py-2 font-mono text-xs">{driver.id}</td>
                    <td className="px-4 py-2">{driver.businessName || driver.fullName || '-'}</td>
                    <td className="px-4 py-2">{driver.fraudScore?.toFixed(2) || '-'}</td>
                    <td className="px-4 py-2">{driver.suspiciousReason || 'High cancellation rate'}</td>
                    <td className="px-4 py-2">
                      <button
                        onClick={() => suspendDriver(driver.id)}
                        className="bg-red-600 hover:bg-red-500 px-2 py-1 rounded text-xs"
                      >
                        Suspend
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Emergency Alerts (Placeholder – can be extended) */}
      <div className="bg-gray-800 rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4">Emergency Alerts</h2>
        <p className="text-gray-400">SOS events will appear here. No active alerts.</p>
      </div>
    </div>
  );
};
